import Info from "assets/Info.svg";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  actions as detailTransactionActions,
  detailTransactionSelector,
} from "reducers/detailTransaction";
import { sessionSelector } from "reducers/session";
import { AppDispatch, RootState } from "store";
import { FinishModalPropsType } from "./types";
import "./finishModal.scss";
import { FilterTransactionsType } from "sagas/types/transactions";
import {
  actions as transactionActions,
  transactionsSelector,
} from "reducers/transactions";
import { PayloadTransactionType } from "sagas/types/detailTransactions";
import "../i18n";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { BackButton } from "../backButton/BackButton";
import { RadioButton } from "@pickit/pickit-components";
import { requiresDeliveryEvidence } from "../proofOfDeliveryRequired";
import { DEFAULT_COUNTRY } from "hooks/useSession";

const FinishModal: React.FC<FinishModalPropsType> = ({
  detailTransaction,
  countryCode,
  getDetailTransactionFinishLostRequest,
  getDetailTransactionFinishReturnedRequest,
  getDetailTransactionDniDeliveredRequest,
  back,
  next,
  dniFinish,
  undelivered,
  getTransactions,
  filters,
  filtersExtra,
  testId
}): JSX.Element => {
  const { t } = useTranslation();
  const [RadioActive, setRadioActive] = useState(false);
  const [checkBoxSelected, setcheckBoxSelected] = useState("");
  const finishStates = {
    LOST: "LOST",
    RETURNED: "RETURNED",
    DELIVERED: "DELIVERED",
  };
  const deliverableStates = [6, 7];

  const needsEvidenceStep = useMemo(
    () => requiresDeliveryEvidence(detailTransaction),
    [detailTransaction]
  );

  const destination = detailTransaction?.destination as Record<string, unknown> | undefined;
  const latitude = useMemo(
    () =>
      (destination?.latitude as number) ??
      (typeof destination?.lat === "number" ? destination.lat : 0),
    [destination]
  );
  const longitude = useMemo(
    () =>
      (destination?.longitude as number) ??
      (typeof destination?.lng === "number" ? destination.lng : 0),
    [destination]
  );

  const handleCheckboxClick = (value: string) => {
    setcheckBoxSelected(value);
    setRadioActive(true);
  };

  const filtersPayload = {
    ...filters,
    offset: 0,
  }

  const finishTransaction = () => {
    switch (checkBoxSelected) {
      case finishStates.DELIVERED:
        if (needsEvidenceStep) {
          next(dniFinish);
        } else {
          getDetailTransactionDniDeliveredRequest({
            params: {
              name: null,
              lastName: null,
              identificationNumber: null,
              relationship: null,
              photo: null,
              sign: null,
              finishByAdmin: true,
              ...(latitude !== 0 || longitude !== 0 ? { latitude, longitude } : {}),
            },
            id: detailTransaction.transaction.id.toString(),
            callBack: () => {
              getTransactions(filtersPayload);
              back();
            },
            countryCode: countryCode ?? DEFAULT_COUNTRY,
            latitude,
            longitude,
          });
        }
        break;
      case finishStates.RETURNED:
        if (detailTransaction.transaction.state.id === 8) {
          getDetailTransactionFinishReturnedRequest({
            id: detailTransaction.transaction.id.toString(),
            callBack: () => {
              getTransactions(filtersPayload);
            },
          });
        } else {
          next(undelivered);
        }
        break;
      case finishStates.LOST:
        getDetailTransactionFinishLostRequest({
          id: detailTransaction.transaction.id.toString(),
          callBack: () => {
            getTransactions(filtersPayload);
          },
        });
        break;
    }
  };

  return (
    <form
      className="modal-transaction-finish-form"
      onSubmit={finishTransaction}
    >
      <div className="container-finish">
        <BackButton back={back} />
        <div className="modal-transaction-finish-container">
          <img
            className="modal-transaction-finish-img"
            src={Info}
            alt="informacionIcon"
          />
          <h3>{t("detailTransaction:selectFinalState")}</h3>
          <hr className="modal-transaction-finish-separate" />
          <p>
            {t("detailTransaction:infoTransaction")}
            <b className="transaction-code">
              {" "}
              {detailTransaction?.transaction?.transactionCode}
            </b>
            {t("detailTransaction:infoFinalState")}
          </p>
          <div className="modal-transaction-finish-inputs">
            <div
              className={classNames("flexItems", {
                "flexItems-items-center": !deliverableStates.includes(
                  detailTransaction?.transaction?.state?.id
                ),
              })}
            >
              <RadioButton
                name="state"
                value={finishStates.LOST}
                id={finishStates.LOST}
                label={t("detailTransaction:lost")}
                meta={{}}
                input={{
                  name: "state",
                  value: finishStates.LOST,
                  onBlur: () => {},
                  onFocus: () => {},
                  onChange(event) {
                    handleCheckboxClick(event);
                  },
                }}
                testId={`${testId}-state-lost`}
              />
            </div>
            {deliverableStates.includes(
              detailTransaction.transaction.state.id
            ) && (
              <div className="flexItems">
                <RadioButton
                  name="state"
                  id={finishStates.DELIVERED}
                  value={finishStates.DELIVERED}
                  label={t("detailTransaction:delivered")}
                  meta={{}}
                  input={{
                    name: "state",
                    value: finishStates.DELIVERED,
                    onBlur: () => {},
                    onFocus: () => {},
                    onChange(event) {
                      handleCheckboxClick(event);
                    },
                  }}
                  testId={`${testId}-state-delivered`}
                />
              </div>
            )}
            <div className="flexItems">
              <RadioButton
                name="state"
                value={finishStates.RETURNED}
                id={finishStates.RETURNED}
                label={t("detailTransaction:returned")}
                meta={{}}
                input={{
                  name: "state",
                  value: finishStates.RETURNED,
                  onBlur: () => {},
                  onFocus: () => {},
                  onChange(event) {
                    handleCheckboxClick(event);
                  },
                }}
                testId={`${testId}-state-returned`}
              />
            </div>
          </div>
        </div>
        <div className="flex-center">
          <button
            type="button"
            onClick={finishTransaction}
            className="finish-button"
            disabled={!RadioActive}
          >
            {t("detailTransaction:finish")}
          </button>
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = (state: RootState) => ({
  detailTransaction: detailTransactionSelector(state).detailTransaction,
  filters: transactionsSelector(state).filters,
  countryCode: sessionSelector(state).countryCode,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getTransactions: (params: FilterTransactionsType) => {
    dispatch(transactionActions.getTransactionsRequest(params));
  },
  getDetailTransactionFinishReturnedRequest: (
    params: PayloadTransactionType
  ) => {
    dispatch(
      detailTransactionActions.getDetailTransactionFinishReturnedRequest(params)
    );
  },
  getDetailTransactionFinishLostRequest: (params: PayloadTransactionType) => {
    dispatch(
      detailTransactionActions.getDetailTransactionFinishLostRequest(params)
    );
  },
  getDetailTransactionDniDeliveredRequest: (payload: {
    params: import("sagas/types/detailTransactions").postDnideliveredRequestType;
    id: string;
    callBack: Function;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
  }) => {
    dispatch(detailTransactionActions.getDetailTransactionDniDeliveredRequest(payload));
  },
});
export default connect(mapStateToProps, mapDispatchToProps)(FinishModal);
